/**
 * XRSessionNegotiator — selects WebGL or WebGPU binding at XR session start.
 *
 * Negotiation algorithm:
 * 1. If allowWebGpuXr=true AND navigator.gpu exists → try WebGPU-first
 * 2. WebGPU attempt: request adapter with xrCompatible, request session with 'webgpu' optional feature
 * 3. If WebGPU fails or 'webgpu' not in enabledFeatures → fallback to WebGL
 * 4. WebGL: standard session request with optional 'layers' feature
 */

import { Logger } from '../core/Logger.js';

export type BindingStrategy = 'try-webgpu-first' | 'webgl-only';

export interface NegotiationOptions {
  /** Enable experimental WebGPU XR binding attempt. */
  allowWebGpuXr: boolean;
  /** Base XR session init (requiredFeatures, optionalFeatures). */
  requiredFeatures?: string[];
  optionalFeatures?: string[];
  /** false = base-layer path: never request the 'layers' session feature. */
  useLayersApi?: boolean;
  /** DOM overlay config for AR. */
  domOverlay?: { root: HTMLElement };
  /** Depth sensing config for AR. */
  depthSensing?: {
    usagePreference: string[];
    dataFormatPreference: string[];
  };
}

export interface NegotiationResult {
  session: XRSession;
  binding: 'webgl' | 'webgpu';
  enabledFeatures: Set<string>;
  /** If binding='webgpu', the adapter+device used. */
  gpuDevice?: GPUDevice;
}

/**
 * Pure decision logic: choose binding strategy.
 */
export function chooseBindingStrategy(opts: {
  allowWebGpuXr: boolean;
  hasWebGPU: boolean;
}): BindingStrategy {
  if (opts.allowWebGpuXr && opts.hasWebGPU) return 'try-webgpu-first';
  return 'webgl-only';
}

/**
 * Build XRSessionInit based on strategy.
 */
export function buildSessionInit(
  _mode: XRSessionMode,
  config: { requiredFeatures?: string[]; optionalFeatures?: string[]; useLayersApi?: boolean },
  strategy: BindingStrategy,
): XRSessionInit {
  let required = [...(config.requiredFeatures ?? [])];
  let optional = [...(config.optionalFeatures ?? [])];

  if (config.useLayersApi === false) {
    // The Layers opt-out MUST also strip the session feature: per the WebXR
    // Layers spec, `updateRenderState({ baseLayer })` THROWS on a session
    // created with 'layers' enabled — requesting the feature while forcing
    // the base-layer path made every on-device setSession fail (black flash,
    // instant exit — Quest 3 field report 2026-08-16).
    required = required.filter((f) => f !== 'layers');
    optional = optional.filter((f) => f !== 'layers');
  } else if (!optional.includes('layers') && !required.includes('layers')) {
    // Include 'layers' as optional for Layers API detection
    optional.push('layers');
  }

  // For WebGPU attempt, include 'webgpu' as optional feature
  if (strategy === 'try-webgpu-first') {
    if (!optional.includes('webgpu') && !required.includes('webgpu')) {
      optional.push('webgpu');
    }
  }

  return {
    requiredFeatures: required.length > 0 ? required : undefined,
    optionalFeatures: optional.length > 0 ? optional : undefined,
  };
}

/**
 * Request an immersive XR session with negotiated binding.
 *
 * Attempts WebGPU-on-WebXR first if enabled, falling back to WebGL.
 * Must be called from a user gesture handler.
 */
export async function requestImmersiveSession(
  mode: 'immersive-vr' | 'immersive-ar',
  opts: NegotiationOptions,
): Promise<NegotiationResult> {
  const hasWebGPU = typeof navigator !== 'undefined' && 'gpu' in navigator;
  const strategy = chooseBindingStrategy({
    allowWebGpuXr: opts.allowWebGpuXr,
    hasWebGPU,
  });

  const xr = navigator.xr;
  if (!xr) throw new Error('WebXR not available');

  // ─── WebGPU-first attempt ─────────────────────────────────────────
  if (strategy === 'try-webgpu-first') {
    try {
      Logger.info('XRNegotiator', 'Attempting WebGPU XR binding...');

      // Request xrCompatible adapter (per WebXR-WebGPU binding proposal)
      const adapter = await navigator.gpu.requestAdapter({
        xrCompatible: true,
      } as any);

      if (!adapter) {
        throw new Error('No xrCompatible GPU adapter available');
      }

      const device = await adapter.requestDevice();
      let deviceDestroyed = false;

      try {
        // Build session init with 'webgpu' feature
        const sessionInit = buildSessionInit(mode, {
          requiredFeatures: opts.requiredFeatures,
          optionalFeatures: opts.optionalFeatures,
          useLayersApi: opts.useLayersApi,
        }, 'try-webgpu-first');

        // Add AR-specific options
        if (opts.domOverlay) {
          (sessionInit as any).domOverlay = opts.domOverlay;
        }
        if (opts.depthSensing) {
          (sessionInit as any).depthSensing = opts.depthSensing;
        }

        const session = await xr.requestSession(mode, sessionInit);

        // Check if 'webgpu' was actually enabled
        const enabledFeatures = new Set<string>(
          (session as any).enabledFeatures ?? [],
        );

        if (enabledFeatures.has('webgpu')) {
          Logger.info('XRNegotiator', 'WebGPU XR binding negotiated successfully');
          return { session, binding: 'webgpu', enabledFeatures, gpuDevice: device };
        }

        // Session created but without WebGPU — end it and fall through
        Logger.info('XRNegotiator', 'Session created but WebGPU feature not enabled, falling back to WebGL');
        await session.end();
        device.destroy();
        deviceDestroyed = true;
      } catch (innerErr) {
        if (!deviceDestroyed) device.destroy();
        throw innerErr;
      }
    } catch (err) {
      Logger.info('XRNegotiator', `WebGPU XR attempt failed: ${err}, falling back to WebGL`);
    }
  }

  // ─── WebGL fallback ───────────────────────────────────────────────
  Logger.info('XRNegotiator', 'Using WebGL XR binding');

  const sessionInit = buildSessionInit(mode, {
    requiredFeatures: opts.requiredFeatures,
    optionalFeatures: opts.optionalFeatures,
    useLayersApi: opts.useLayersApi,
  }, 'webgl-only');

  if (opts.domOverlay) {
    (sessionInit as any).domOverlay = opts.domOverlay;
  }
  if (opts.depthSensing) {
    (sessionInit as any).depthSensing = opts.depthSensing;
  }

  const session = await xr.requestSession(mode, sessionInit);
  const enabledFeatures = new Set<string>(
    (session as any).enabledFeatures ?? [],
  );

  return { session, binding: 'webgl', enabledFeatures };
}
